import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatMoney } from "@/lib/api";
import { useOutlets } from "@/lib/outlets";
import ShiftsEditor from "@/components/ShiftsEditor";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, KeyRound, X, Coffee, Store, Users, UserCog } from "lucide-react";

export default function Admin() {
  const { outlet } = useOutletContext();
  const [tab, setTab] = useState("menu");

  if (!outlet) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="border-b border-border px-6 pt-4">
        <div className="flex items-baseline justify-between mb-3">
          <h1 className="font-heading text-3xl font-bold">Admin</h1>
          <div className="text-xs text-muted-foreground font-mono">{outlet.name}</div>
        </div>
        <nav className="flex gap-1 flex-wrap">
          {[
            { id: "menu", label: "Menu" },
            { id: "outlets", label: "Outlets" },
            { id: "franchisees", label: "Franchisees" },
            { id: "staff", label: "Staff" },
            { id: "shifts", label: "Shifts" },
            { id: "pin", label: "PIN" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-xs font-heading uppercase tracking-[0.15em] border-b-2 transition-colors ${
                tab === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto thin-scroll p-6">
        {tab === "menu" && <MenuEditor outlet={outlet} />}
        {tab === "outlets" && <OutletsEditor />}
        {tab === "franchisees" && <FranchiseesEditor />}
        {tab === "staff" && <StaffEditor />}
        {tab === "shifts" && <ShiftsEditor />}
        {tab === "pin" && <PinEditor />}
      </div>
    </div>
  );
}

function MenuEditor({ outlet }) {
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [showCatForm, setShowCatForm] = useState(false);
  const [showProdForm, setShowProdForm] = useState(false);
  const [editingProd, setEditingProd] = useState(null);
  const [editingCat, setEditingCat] = useState(null);

  const reload = async () => {
    const [c, p] = await Promise.all([
      api.get(`/categories?outlet_id=${outlet.id}`),
      api.get(`/products?outlet_id=${outlet.id}`),
    ]);
    setCats(c.data);
    setProds(p.data);
  };
  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [outlet]);

  const delCat = async (id) => {
    if (!window.confirm("Delete this category? All its products will be removed.")) return;
    await api.delete(`/categories/${id}`);
    toast.success("Category deleted");
    reload();
  };
  const delProd = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    reload();
  };

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Categories */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-semibold">Categories</h2>
          <button
            data-testid="add-category-btn"
            onClick={() => { setEditingCat(null); setShowCatForm(true); }}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-xs font-heading uppercase tracking-widest hover:opacity-90 active:scale-[0.98] transition-all"
          >
            <Plus strokeWidth={2} className="w-4 h-4" /> Add category
          </button>
        </div>
        {cats.length === 0 ? (
          <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No categories yet. Create one to start adding products.
          </div>
        ) : (
          <div className="grid gap-2" data-testid="categories-list">
            {cats.map((c) => (
              <div key={c.id} className="flex items-center justify-between bg-card border border-border px-4 py-3" data-testid={`admin-cat-${c.id}`}>
                <div className="font-heading font-semibold">{c.name}</div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`edit-cat-${c.id}`}
                    onClick={() => { setEditingCat(c); setShowCatForm(true); }}
                    className="p-2 hover:bg-muted"
                  >
                    <Pencil strokeWidth={1.5} className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`delete-cat-${c.id}`}
                    onClick={() => delCat(c.id)}
                    className="p-2 hover:bg-muted text-destructive"
                  >
                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Products */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-semibold">Products</h2>
          <button
            data-testid="add-product-btn"
            disabled={cats.length === 0}
            onClick={() => { setEditingProd(null); setShowProdForm(true); }}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-xs font-heading uppercase tracking-widest hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-40"
          >
            <Plus strokeWidth={2} className="w-4 h-4" /> Add product
          </button>
        </div>
        {prods.length === 0 ? (
          <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            {cats.length === 0 ? "Create a category first." : "No products yet."}
          </div>
        ) : (
          <div className="grid gap-2" data-testid="products-list">
            {prods.map((p) => {
              const cat = cats.find((c) => c.id === p.category_id);
              return (
                <div key={p.id} className="flex items-center justify-between bg-card border border-border px-4 py-3" data-testid={`admin-prod-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-muted flex items-center justify-center overflow-hidden">
                      {p.image_url ? (
                        <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Coffee strokeWidth={1.25} className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <div className="font-heading font-semibold">{p.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {cat?.name || "—"} ·{" "}
                        {p.variants?.length
                          ? `${p.variants.length} sizes · ₹${formatMoney(Math.min(...p.variants.map((v) => v.price)))}–₹${formatMoney(Math.max(...p.variants.map((v) => v.price)))}`
                          : `₹${formatMoney(p.price)}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`edit-prod-${p.id}`}
                      onClick={() => { setEditingProd(p); setShowProdForm(true); }}
                      className="p-2 hover:bg-muted"
                    >
                      <Pencil strokeWidth={1.5} className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-prod-${p.id}`}
                      onClick={() => delProd(p.id)}
                      className="p-2 hover:bg-muted text-destructive"
                    >
                      <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {showCatForm && (
        <CategoryForm
          outletId={outlet.id}
          initial={editingCat}
          onClose={() => setShowCatForm(false)}
          onSaved={() => { setShowCatForm(false); reload(); }}
        />
      )}
      {showProdForm && (
        <ProductForm
          outletId={outlet.id}
          categories={cats}
          initial={editingProd}
          onClose={() => setShowProdForm(false)}
          onSaved={() => { setShowProdForm(false); reload(); }}
        />
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border w-full max-w-md p-6 animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-semibold">{title}</h3>
          <button data-testid="modal-close" onClick={onClose} className="p-1 hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CategoryForm({ outletId, initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      if (initial) {
        await api.put(`/categories/${initial.id}`, { name });
        toast.success("Category updated");
      } else {
        await api.post("/categories", { outlet_id: outletId, name });
        toast.success("Category created");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={initial ? "Edit category" : "New category"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          <input
            data-testid="cat-name-input"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-background border border-border px-3 py-2 focus:outline-none focus:border-primary"
          />
        </Field>
        <button data-testid="cat-save-btn" disabled={busy} className="w-full bg-primary text-primary-foreground py-3 font-heading uppercase tracking-widest text-sm hover:opacity-90 active:scale-[0.98] disabled:opacity-40">
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </Modal>
  );
}

function ProductForm({ outletId, categories, initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [price, setPrice] = useState(initial?.price?.toString() || "");
  const [categoryId, setCategoryId] = useState(initial?.category_id || categories[0]?.id || "");
  const [imageUrl, setImageUrl] = useState(initial?.image_url || "");
  const [useVariants, setUseVariants] = useState(
    Array.isArray(initial?.variants) && initial.variants.length > 0
  );
  const [variants, setVariants] = useState(
    initial?.variants?.length ? initial.variants.map((v) => ({ name: v.name, price: v.price.toString() })) : [{ name: "Small", price: "" }, { name: "Regular", price: "" }, { name: "Large", price: "" }]
  );
  const [busy, setBusy] = useState(false);

  const addVariant = () => setVariants((vs) => [...vs, { name: "", price: "" }]);
  const removeVariant = (idx) => setVariants((vs) => vs.filter((_, i) => i !== idx));
  const updateVariant = (idx, field, val) =>
    setVariants((vs) => vs.map((v, i) => (i === idx ? { ...v, [field]: val } : v)));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required");
    if (!categoryId) return toast.error("Pick a category");

    let normalizedVariants = null;
    let priceNum;

    if (useVariants) {
      const cleaned = variants
        .map((v) => ({ name: v.name.trim(), price: parseFloat(v.price) }))
        .filter((v) => v.name && !isNaN(v.price));
      if (cleaned.length < 2) return toast.error("Add at least 2 sizes (e.g. Regular + Large)");
      if (cleaned.some((v) => v.price < 0)) return toast.error("Variant prices must be ≥ 0");
      const names = cleaned.map((v) => v.name.toLowerCase());
      if (new Set(names).size !== names.length) return toast.error("Variant names must be unique");
      normalizedVariants = cleaned;
      priceNum = Math.min(...cleaned.map((v) => v.price)); // base = cheapest variant
    } else {
      priceNum = parseFloat(price);
      if (isNaN(priceNum) || priceNum < 0) return toast.error("Enter a valid price");
    }

    setBusy(true);
    try {
      const payload = {
        name,
        price: priceNum,
        category_id: categoryId,
        image_url: imageUrl || null,
        variants: normalizedVariants || [],
      };
      if (initial) {
        await api.put(`/products/${initial.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", { ...payload, outlet_id: outletId });
        toast.success("Product created");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={initial ? "Edit product" : "New product"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          <input data-testid="prod-name-input" autoFocus value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-background border border-border px-3 py-2 focus:outline-none focus:border-primary" />
        </Field>

        <div className="flex items-center gap-2">
          <input
            id="use-variants"
            data-testid="prod-use-variants"
            type="checkbox"
            checked={useVariants}
            onChange={(e) => setUseVariants(e.target.checked)}
            className="w-4 h-4 accent-primary"
          />
          <label htmlFor="use-variants" className="text-sm select-none cursor-pointer">
            This product has sizes (e.g. Small / Regular / Large)
          </label>
        </div>

        {!useVariants ? (
          <Field label="Price">
            <input data-testid="prod-price-input" type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="w-full bg-background border border-border px-3 py-2 font-mono focus:outline-none focus:border-primary" />
          </Field>
        ) : (
          <div className="space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Sizes</div>
            {variants.map((v, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`prod-variant-row-${idx}`}>
                <input
                  data-testid={`prod-variant-name-${idx}`}
                  placeholder="Size"
                  value={v.name}
                  onChange={(e) => updateVariant(idx, "name", e.target.value)}
                  className="flex-1 bg-background border border-border px-3 py-2 focus:outline-none focus:border-primary"
                />
                <input
                  data-testid={`prod-variant-price-${idx}`}
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Price"
                  value={v.price}
                  onChange={(e) => updateVariant(idx, "price", e.target.value)}
                  className="w-24 bg-background border border-border px-3 py-2 font-mono focus:outline-none focus:border-primary"
                />
                <button
                  type="button"
                  data-testid={`prod-variant-remove-${idx}`}
                  onClick={() => removeVariant(idx)}
                  className="p-2 hover:bg-muted text-destructive"
                  disabled={variants.length <= 1}
                >
                  <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              data-testid="prod-variant-add"
              onClick={addVariant}
              className="text-xs font-heading uppercase tracking-widest text-primary hover:opacity-80 flex items-center gap-1 mt-1"
            >
              <Plus strokeWidth={2} className="w-3.5 h-3.5" /> Add size
            </button>
          </div>
        )}

        <Field label="Category">
          <select data-testid="prod-cat-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-full bg-background border border-border px-3 py-2 focus:outline-none focus:border-primary">
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Image URL (optional)">
          <input data-testid="prod-image-input" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://…" className="w-full bg-background border border-border px-3 py-2 focus:outline-none focus:border-primary" />
        </Field>
        <button data-testid="prod-save-btn" disabled={busy} className="w-full bg-primary text-primary-foreground py-3 font-heading uppercase tracking-widest text-sm hover:opacity-90 active:scale-[0.98] disabled:opacity-40">
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{label}</span>
      {children}
    </label>
  );
}

function PinEditor() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("New PIN does not match confirmation");
    if (!/^\d{4,8}$/.test(next)) return toast.error("PIN must be 4–8 digits");
    setBusy(true);
    try {
      await api.post("/auth/change-pin", { current_pin: current, new_pin: next });
      toast.success("Master PIN updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change PIN");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="max-w-md space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <KeyRound strokeWidth={1.5} className="w-5 h-5 text-primary" />
        <h2 className="font-heading text-xl font-semibold">Change Master PIN</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        The master PIN unlocks the terminal. Choose a 4–8 digit code.
      </p>
      <Field label="Current PIN">
        <input data-testid="current-pin-input" type="password" inputMode="numeric" value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full bg-background border border-border px-3 py-2 font-mono focus:outline-none focus:border-primary" />
      </Field>
      <Field label="New PIN">
        <input data-testid="new-pin-input" type="password" inputMode="numeric" value={next} onChange={(e) => setNext(e.target.value)} className="w-full bg-background border border-border px-3 py-2 font-mono focus:outline-none focus:border-primary" />
      </Field>
      <Field label="Confirm new PIN">
        <input data-testid="confirm-pin-input" type="password" inputMode="numeric" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full bg-background border border-border px-3 py-2 font-mono focus:outline-none focus:border-primary" />
      </Field>
      <button data-testid="save-pin-btn" disabled={busy} className="w-full bg-primary text-primary-foreground py-3 font-heading uppercase tracking-widest text-sm hover:opacity-90 active:scale-[0.98] disabled:opacity-40">
        {busy ? "Updating…" : "Update PIN"}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Franchisees
// ---------------------------------------------------------------------------
function FranchiseesEditor() {
  const { refresh } = useOutlets();
  const [items, setItems] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const reload = async () => {
    const [f, o] = await Promise.all([api.get("/franchisees"), api.get("/outlets")]);
    setItems(f.data);
    setOutlets(o.data);
  };
  useEffect(() => { reload(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this franchisee?")) return;
    try {
      await api.delete(`/franchisees/${id}`);
      toast.success("Franchisee deleted");
      await reload();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
            <Users strokeWidth={1.5} className="w-5 h-5 text-primary" /> Franchisee Partners
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Business partners who own one or more Teaboy outlets.</p>
        </div>
        <button
          data-testid="add-franchisee-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-xs font-heading uppercase tracking-widest hover:opacity-90 active:scale-[0.98]"
        >
          <Plus strokeWidth={2} className="w-4 h-4" /> Add partner
        </button>
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No franchisees yet. Add your first partner to start assigning outlets.
        </div>
      ) : (
        <div className="grid gap-2" data-testid="franchisees-list">
          {items.map((f) => {
            const outletCount = outlets.filter((o) => o.franchisee_id === f.id).length;
            return (
              <div key={f.id} className="flex items-center justify-between bg-card border border-border px-4 py-3" data-testid={`admin-franchisee-${f.id}`}>
                <div>
                  <div className="font-heading font-semibold">{f.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {outletCount} outlet{outletCount !== 1 && "s"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`edit-franchisee-${f.id}`}
                    onClick={() => { setEditing(f); setShowForm(true); }}
                    className="p-2 hover:bg-muted"
                  >
                    <Pencil strokeWidth={1.5} className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`delete-franchisee-${f.id}`}
                    onClick={() => del(f.id)}
                    className="p-2 hover:bg-muted text-destructive"
                  >
                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <NameForm
          title={editing ? "Edit franchisee" : "New franchisee"}
          initialName={editing?.name || ""}
          placeholder="e.g. Kondapur Partners"
          testId="franchisee"
          onClose={() => setShowForm(false)}
          onSubmit={async (name) => {
            if (editing) {
              await api.put(`/franchisees/${editing.id}`, { name });
              toast.success("Franchisee updated");
            } else {
              await api.post("/franchisees", { name });
              toast.success("Franchisee added");
            }
            setShowForm(false);
            await reload();
            refresh();
          }}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Outlets (stores)
// ---------------------------------------------------------------------------
function OutletsEditor() {
  const { refresh } = useOutlets();
  const [items, setItems] = useState([]);
  const [franchisees, setFranchisees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    const [o, f] = await Promise.all([api.get("/outlets"), api.get("/franchisees")]);
    setItems(o.data);
    setFranchisees(f.data);
  };
  useEffect(() => { reload(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this outlet? Its menu will also be removed. Orders must be cleared first.")) return;
    try {
      await api.delete(`/outlets/${id}`);
      toast.success("Outlet deleted");
      await reload();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
            <Store strokeWidth={1.5} className="w-5 h-5 text-primary" /> Outlets (Stores)
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Each physical Teaboy store. Every outlet owns its own menu.</p>
        </div>
        <button
          data-testid="add-outlet-btn"
          disabled={franchisees.length === 0}
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-xs font-heading uppercase tracking-widest hover:opacity-90 active:scale-[0.98] disabled:opacity-40"
        >
          <Plus strokeWidth={2} className="w-4 h-4" /> Add outlet
        </button>
      </div>
      {franchisees.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Add a franchisee partner first (Admin → Franchisees).
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No outlets yet.
        </div>
      ) : (
        <div className="grid gap-2" data-testid="outlets-list">
          {items.map((o) => {
            const f = franchisees.find((fr) => fr.id === o.franchisee_id);
            return (
              <div key={o.id} className="flex items-center justify-between bg-card border border-border px-4 py-3" data-testid={`admin-outlet-${o.id}`}>
                <div>
                  <div className="font-heading font-semibold">{o.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{f?.name || "—"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`edit-outlet-${o.id}`}
                    onClick={() => { setEditing(o); setShowForm(true); }}
                    className="p-2 hover:bg-muted"
                  >
                    <Pencil strokeWidth={1.5} className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`delete-outlet-${o.id}`}
                    onClick={() => del(o.id)}
                    className="p-2 hover:bg-muted text-destructive"
                  >
                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <OutletForm
          initial={editing}
          franchisees={franchisees}
          onClose={() => setShowForm(false)}
          onSaved={async () => {
            setShowForm(false);
            await reload();
            refresh();
          }}
        />
      )}
    </div>
  );
}


function NameForm({ title, initialName, placeholder, testId, onClose, onSubmit }) {
  const [name, setName] = useState(initialName || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      await onSubmit(name.trim());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          <input
            data-testid={`${testId}-name-input`}
            autoFocus
            value={name}
            placeholder={placeholder}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-background border border-border px-3 py-2 focus:outline-none focus:border-primary"
          />
        </Field>
        <button data-testid={`${testId}-save-btn`} disabled={busy} className="w-full bg-primary text-primary-foreground py-3 font-heading uppercase tracking-widest text-sm hover:opacity-90 active:scale-[0.98] disabled:opacity-40">
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </Modal>
  );
}


function OutletForm({ initial, franchisees, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [franchiseeId, setFranchiseeId] = useState(initial?.franchisee_id || franchisees[0]?.id || "");
  const [copyMenuFrom, setCopyMenuFrom] = useState("");
  const [outletsAvailable, setOutletsAvailable] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (initial) return; // don't need copy list when editing
    api.get("/outlets").then(({ data }) => setOutletsAvailable(data));
  }, [initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required");
    if (!franchiseeId) return toast.error("Pick a franchisee");
    setBusy(true);
    try {
      if (initial) {
        await api.put(`/outlets/${initial.id}`, { name: name.trim() });
        toast.success("Outlet updated");
      } else {
        await api.post("/outlets", {
          name: name.trim(),
          franchisee_id: franchiseeId,
          copy_menu_from_outlet_id: copyMenuFrom || null,
        });
        toast.success(copyMenuFrom ? "Outlet added — menu copied" : "Outlet added");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={initial ? "Edit outlet" : "New outlet"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Outlet name">
          <input
            data-testid="outlet-name-input"
            autoFocus
            value={name}
            placeholder="e.g. Teaboy Kondapur"
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-background border border-border px-3 py-2 focus:outline-none focus:border-primary"
          />
        </Field>
        <Field label="Franchisee partner">
          <select
            data-testid="outlet-franchisee-select"
            value={franchiseeId}
            onChange={(e) => setFranchiseeId(e.target.value)}
            disabled={!!initial}
            className="w-full bg-background border border-border px-3 py-2 focus:outline-none focus:border-primary disabled:opacity-60"
          >
            {franchisees.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          {initial && <p className="text-[10px] text-muted-foreground font-mono mt-1">Franchisee cannot be changed once created.</p>}
        </Field>

        {!initial && outletsAvailable.length > 0 && (
          <Field label="Copy menu from (optional)">
            <select
              data-testid="outlet-copy-menu-select"
              value={copyMenuFrom}
              onChange={(e) => setCopyMenuFrom(e.target.value)}
              className="w-full bg-background border border-border px-3 py-2 focus:outline-none focus:border-primary"
            >
              <option value="">— Empty menu —</option>
              {outletsAvailable.map((o) => (
                <option key={o.id} value={o.id}>Copy from: {o.name}</option>
              ))}
            </select>
            <p className="text-[10px] text-muted-foreground font-mono mt-1">Duplicates categories & products (prices too).</p>
          </Field>
        )}

        <button data-testid="outlet-save-btn" disabled={busy} className="w-full bg-primary text-primary-foreground py-3 font-heading uppercase tracking-widest text-sm hover:opacity-90 active:scale-[0.98] disabled:opacity-40">
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </Modal>
  );
}



// ---------------------------------------------------------------------------
// Staff (Users) — owner only
// ---------------------------------------------------------------------------
const ROLE_OPTIONS = [
  { value: "owner", label: "Owner", hint: "Full access" },
  { value: "cashier", label: "Cashier", hint: "Terminal + Reports" },
  { value: "viewer", label: "Viewer", hint: "Dashboard + Reports only" },
];

function StaffEditor() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    const { data } = await api.get("/users");
    setItems(data);
  };
  useEffect(() => { reload(); }, []);

  const del = async (id) => {
    if (!window.confirm("Remove this staff member? Their PIN will stop working.")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Staff removed");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
            <UserCog strokeWidth={1.5} className="w-5 h-5 text-primary" /> Staff & permissions
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Each person unlocks the terminal with their own PIN. Their role decides what they can do.
          </p>
        </div>
        <button
          data-testid="add-staff-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-xs font-heading uppercase tracking-widest hover:opacity-90 active:scale-[0.98]"
        >
          <Plus strokeWidth={2} className="w-4 h-4" /> Add staff
        </button>
      </div>

      <div className="mb-4 bg-accent/40 border border-border px-4 py-3 text-xs font-mono">
        <strong className="font-heading uppercase tracking-widest text-[10px] block mb-1">Roles</strong>
        {ROLE_OPTIONS.map((r) => (
          <div key={r.value} className="text-muted-foreground">
            <span className="font-semibold text-foreground uppercase tracking-widest text-[10px]">{r.label}</span> — {r.hint}
          </div>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No staff yet. The Master PIN unlocks as Owner. Add cashiers so orders track who took them.
        </div>
      ) : (
        <div className="grid gap-2" data-testid="staff-list">
          {items.map((u) => (
            <div key={u.id} className="flex items-center justify-between bg-card border border-border px-4 py-3" data-testid={`admin-user-${u.id}`}>
              <div className="min-w-0">
                <div className="font-heading font-semibold">{u.name}</div>
                <div className="text-xs text-muted-foreground font-mono uppercase tracking-widest flex items-center gap-2">
                  <span>{u.role}</span>
                  <span>·</span>
                  <span className={u.active ? "text-primary" : "text-destructive"}>{u.active ? "Active" : "Disabled"}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`toggle-user-${u.id}`}
                  onClick={() => toggleActive(u)}
                  className="text-xs font-heading uppercase tracking-widest px-2 py-1 border border-border hover:bg-muted"
                >
                  {u.active ? "Disable" : "Enable"}
                </button>
                <button
                  data-testid={`edit-user-${u.id}`}
                  onClick={() => { setEditing(u); setShowForm(true); }}
                  className="p-2 hover:bg-muted"
                >
                  <Pencil strokeWidth={1.5} className="w-4 h-4" />
                </button>
                <button
                  data-testid={`delete-user-${u.id}`}
                  onClick={() => del(u.id)}
                  className="p-2 hover:bg-muted text-destructive"
                >
                  <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <StaffForm
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); reload(); }}
        />
      )}
    </div>
  );
}

function StaffForm({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [role, setRole] = useState(initial?.role || "cashier");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required");
    if (!initial && !/^\d{4,8}$/.test(pin)) return toast.error("PIN must be 4–8 digits");
    if (initial && pin && !/^\d{4,8}$/.test(pin)) return toast.error("PIN must be 4–8 digits (or leave blank to keep)");
    setBusy(true);
    try {
      if (initial) {
        const patch = { name: name.trim(), role };
        if (pin) patch.pin = pin;
        await api.put(`/users/${initial.id}`, patch);
        toast.success("Staff updated");
      } else {
        await api.post("/users", { name: name.trim(), role, pin });
        toast.success("Staff added");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={initial ? "Edit staff" : "New staff member"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          <input
            data-testid="staff-name-input"
            autoFocus
            value={name}
            placeholder="e.g. Ravi"
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-background border border-border px-3 py-2 focus:outline-none focus:border-primary"
          />
        </Field>
        <Field label="Role">
          <select
            data-testid="staff-role-select"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full bg-background border border-border px-3 py-2 focus:outline-none focus:border-primary"
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label} — {r.hint}</option>
            ))}
          </select>
        </Field>
        <Field label={initial ? "PIN (leave blank to keep existing)" : "PIN (4–8 digits)"}>
          <input
            data-testid="staff-pin-input"
            type="password"
            inputMode="numeric"
            value={pin}
            placeholder={initial ? "••••" : "e.g. 4711"}
            onChange={(e) => setPin(e.target.value)}
            className="w-full bg-background border border-border px-3 py-2 font-mono focus:outline-none focus:border-primary"
          />
          <p className="text-[10px] text-muted-foreground font-mono mt-1">Must be unique across all staff and the Master PIN.</p>
        </Field>
        <button data-testid="staff-save-btn" disabled={busy} className="w-full bg-primary text-primary-foreground py-3 font-heading uppercase tracking-widest text-sm hover:opacity-90 active:scale-[0.98] disabled:opacity-40">
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </Modal>
  );
}
